<template>
  <b-card no-body class="apt-page">
    <div class="apt-layout">
      <!-- ===== Sidebar ===== -->
      <aside class="apt-sidebar" :class="{ 'apt-sidebar--open': sidebarOpen }">
        <b-button block class="apt-new-btn" @click="addEvent">
          <feather-icon icon="PlusIcon" size="16" class="mr-50"/>{{ $t('calendar.action.addEvent') }}
        </b-button>

        <!-- Pending invites (events waiting for MY answer) -->
        <div v-if="pendingInvites.length" class="apt-pending mt-2">
          <div class="apt-pending__title">
            <feather-icon icon="BellIcon" size="14" class="mr-25"/>
            {{ $t('calendar.pendingInvites') }}
            <span class="apt-pending__count">{{ pendingInvites.length }}</span>
          </div>
          <div class="apt-pending__list">
            <div v-for="inv in pendingInvites" :key="inv.id" class="apt-request">
              <div class="apt-request__row">
                <span class="apt-request__avatar">{{ initials(inv.title) }}</span>
                <div class="apt-request__main">
                  <div class="apt-request__name">{{ inv.title || $t(`calendarTypes.${inv.type}.name`) }}</div>
                  <div class="apt-request__meta">
                    <feather-icon icon="ClockIcon" size="12" class="mr-25"/>{{ formatInvite(inv.start) }}
                  </div>
                </div>
              </div>
              <div class="apt-request__actions">
                <b-button size="sm" variant="success" :disabled="answering === inv.id"
                          @click="answerInvite(inv, 'APPROVED')">
                  {{ $t('calendar.action.accept') }}
                </b-button>
                <b-button size="sm" variant="outline-danger" :disabled="answering === inv.id"
                          @click="answerInvite(inv, 'REJECTED')">
                  {{ $t('calendar.action.reject') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Type filters = color legend + default duration -->
        <div class="apt-section-title mt-2">
          {{ $t('calendar.types') }}
          <b-link class="apt-section-title__toggle" @click="toggleAllTypes">
            {{ allTypesSelected ? $t('filters.unselectAll') : $t('filters.selectAll') }}
          </b-link>
        </div>
        <div>
          <div v-for="type in typeList" :key="type.value" class="apt-type-row" @click="toggleType(type.value)">
            <span class="apt-filter-check" :class="{ checked: selectedTypes.includes(type.value) }"
                  :style="{ '--apt-c': type.color }">
              <feather-icon v-if="selectedTypes.includes(type.value)" icon="CheckIcon" size="11"/>
            </span>
            <span class="apt-type-row__label">{{ $t(`calendarTypes.${type.value}.name`) }}</span>
            <span class="apt-type-row__duration">{{ type.duration }}'</span>
          </div>
        </div>

        <!-- Status chips (my participation / event status) -->
        <div class="apt-section-title mt-2">{{ $t('calendar.status') }}</div>
        <div class="apt-chips">
          <button v-for="st in statusList" :key="st.key" type="button" class="apt-chip"
                  :class="{ 'is-active': selectedTypes.includes(st.key) }"
                  :style="selectedTypes.includes(st.key) ? { background: st.color, borderColor: st.color, color: '#fff' } : {}"
                  @click="toggleType(st.key)">
            {{ $t(`calendar.${st.key}`) }}
          </button>
        </div>

        <!-- Admin scope: whose calendars to show -->
        <div v-if="hasPermission(permissions.CALENDAR_READ_ALL)" class="apt-admin mt-2">
          <div class="apt-section-title">{{ $t('calendar.adminScope') }}</div>
          <div class="apt-admin__switch">
            <b-form-checkbox switch v-model="showOthers"/>
            <span>{{ $t('calendar.showOtherUsers') }}</span>
          </div>
          <div v-if="showOthers" class="mt-1">
            <user-select-box v-model="selectedUsers" multi :placeholder="$t('calendar.allMyTeam')"/>
            <small class="text-muted d-block mt-25" v-if="!selectedUsers.length">
              <feather-icon icon="InfoIcon" size="12" class="mr-25"/>{{ $t('calendar.teamHint') }}
            </small>
          </div>
        </div>
      </aside>

      <!-- ===== Main ===== -->
      <div class="apt-main">
        <div class="apt-main__toolbar">
          <b-button variant="flat-secondary" class="apt-hamburger btn-icon" @click="sidebarOpen = !sidebarOpen">
            <feather-icon icon="MenuIcon" size="18"/>
          </b-button>
          <span class="apt-count">
            <b-spinner v-if="calLoading" small class="mr-25" style="width: 13px; height: 13px"/>
            <feather-icon v-else icon="CalendarIcon" size="13" class="mr-25"/>
            {{ visibleCount }} {{ $t('calendar.eventsCount') }}
          </span>
          <b-button variant="flat-secondary" class="btn-icon apt-refresh" :disabled="calLoading"
                    v-b-tooltip.hover :title="$t('operations.refresh')" @click="refreshCalendar">
            <feather-icon icon="RefreshCwIcon" size="15" :class="{ 'apt-refresh--spin': calLoading }"/>
          </b-button>
        </div>
        <calendar ref="calendar" v-bind:types="selectedTypes" v-bind:users="usersForCalendar"
                  @events-loaded="onEventsLoaded" @loading="v => calLoading = v"/>
      </div>
    </div>
  </b-card>
</template>

<script>
import Calendar from '@/components/partial/calendar/Calendar'
import { mapActions, mapGetters } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import UserSelectBox from '@/components/widget/select/UserSelectBox'
import { CALENDAR_TYPE_META, CALENDAR_STATUS_META } from '@/model/calendar/calendarMeta'
import { CalendarStatusEnum } from '@/model/calendar/CalendarStatusEnum'

export default {
  name: 'CalendarView',
  components: { Calendar, UserSelectBox },
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  data: () => ({
    selectedTypes: [],
    selectedUsers: [],
    showOthers: false,
    sidebarOpen: false,
    loadedEvents: [],
    answering: null,
    calLoading: false,
  }),
  computed: {
    ...mapGetters('user', ['myId', 'user']),
    typeList() {
      // Only types that exist in the i18n map (backend enum mirror).
      const known = Object.keys(this.$t('calendarTypes'))
      return CALENDAR_TYPE_META.filter(t => known.includes(t.value))
    },
    statusList() {
      return [
        { key: 'approved', color: CALENDAR_STATUS_META.APPROVED },
        { key: 'pending', color: CALENDAR_STATUS_META.PENDING },
        { key: 'rejected', color: CALENDAR_STATUS_META.REJECTED },
        { key: 'cancelled', color: CALENDAR_STATUS_META.CANCELLED },
      ]
    },
    allTypesSelected() {
      return this.typeList.every(t => this.selectedTypes.includes(t.value))
    },
    // Whose calendars the grid loads. Empty + CALENDAR_READ_ALL = whole team
    // (the backend expands it to the user + all his subordinates).
    usersForCalendar() {
      if (!this.hasPermission(this.permissions.CALENDAR_READ_ALL) || !this.showOthers) {
        return [this.myId]
      }
      return this.selectedUsers
    },
    visibleCount() {
      return this.loadedEvents.length
    },
    // Events waiting for MY answer (I'm a participant with status PENDING).
    pendingInvites() {
      const my = this.myId
      return this.loadedEvents
          .filter(ev => ev.status !== CalendarStatusEnum.CANCELLED && ev.userId !== my)
          .filter(ev => {
            const me = (ev.users || []).find(u => u.userId === my)
            return me != null && (me.status == null || me.status === CalendarStatusEnum.PENDING)
          })
          .sort((a, b) => String(a.start).localeCompare(String(b.start)))
          .slice(0, 6)
    },
  },
  created() {
    this.selectedTypes = [...CALENDAR_TYPE_META.map(t => t.value), 'approved', 'pending']
  },
  methods: {
    ...mapActions('calendar', ['updateUserStatus']),
    addEvent() {
      this.$refs['calendar'].onDateClick({ dateStr: this.$moment().format('YYYY-MM-DD HH:mm') })
    },
    refreshCalendar() {
      this.$refs['calendar'].refreshCalendar()
    },
    toggleType(key) {
      const idx = this.selectedTypes.indexOf(key)
      if (idx >= 0) this.selectedTypes.splice(idx, 1)
      else this.selectedTypes.push(key)
    },
    toggleAllTypes() {
      const statusKeys = this.selectedTypes.filter(k => ['approved', 'pending', 'rejected', 'cancelled'].includes(k))
      this.selectedTypes = this.allTypesSelected
          ? [...statusKeys]
          : [...this.typeList.map(t => t.value), ...statusKeys]
    },
    onEventsLoaded(events) {
      this.loadedEvents = Array.isArray(events) ? events : []
    },
    initials(name) {
      if (!name) return '?'
      return String(name).split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase()
    },
    formatInvite(start) {
      const m = this.$moment.serverDateTime(start)
      return m.isValid() ? m.format('ddd D MMM · HH:mm') : String(start)
    },
    answerInvite(inv, status) {
      const $this = this
      this.answering = inv.id
      this.updateUserStatus({ eventId: inv.id, userId: this.myId, status })
          .then(
              () => {
                $this.showSuccessNotification($this.$t('globalMessages.updateDataSuccess', { system: $this.$t('menu.calendar') }))
                $this.$refs['calendar'].refreshCalendar()
              },
              err => $this.showErrorNotification($this.$t('globalMessages.updateDataError', { system: $this.$t('menu.calendar') }), $this.getErrorDescription(err))
          )
          .finally(() => { $this.answering = null })
    },
  }
}
</script>

<style lang="scss" scoped>
.apt-page { overflow: visible; }

.apt-layout {
  display: flex;
  min-height: 70vh;
  position: relative;
}

/* ===== Sidebar ===== */
.apt-sidebar {
  flex: 0 0 250px;
  width: 250px;
  border-right: 1px solid #ebe9f1;
  padding: 1.25rem 1rem;
}

.apt-new-btn {
  background: linear-gradient(120deg, #7367f0, #9e95f5) !important;
  border: none !important;
  box-shadow: 0 4px 14px rgba(115, 103, 240, .4);
  font-weight: 600;
}

.apt-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: .78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .4px;
  color: #a6a4b0;
  margin-bottom: .5rem;

  &__toggle {
    font-size: .7rem;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 600;
  }
}

/* Type rows: colored checkbox = legend, duration right-aligned */
.apt-type-row {
  display: flex;
  align-items: center;
  gap: .55rem;
  padding: .35rem .45rem;
  border-radius: .45rem;
  cursor: pointer;
  user-select: none;
  transition: background .12s ease;

  &:hover { background: #f8f8fb; }

  &__label {
    flex: 1;
    min-width: 0;
    font-size: .85rem;
    color: #5e5873;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__duration {
    font-size: .72rem;
    color: #b9b9c3;
    font-weight: 600;
  }
}
.apt-filter-check {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 2px solid var(--apt-c);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all .12s ease;

  &.checked { background: var(--apt-c); }
}

/* Status chips */
.apt-chips {
  display: flex;
  flex-wrap: wrap;
  gap: .35rem;
}
.apt-chip {
  border-radius: 2rem;
  padding: .3rem .6rem;
  font-size: .75rem;
  font-weight: 600;
  border: 1px solid #d8d6de;
  background: transparent;
  color: #6e6b7b;
  cursor: pointer;
  transition: all .15s ease;

  &:hover { border-color: #7367f0; color: #7367f0; }
}

/* Admin scope */
.apt-admin {
  border-top: 1px dashed #ebe9f1;
  padding-top: 1rem;

  &__switch {
    display: flex;
    align-items: center;
    gap: .2rem;
    font-size: .85rem;
    color: #5e5873;

    ::v-deep .custom-switch { flex: 0 0 3.4rem; }
  }
}

/* Pending invites panel */
.apt-pending {
  &__title {
    display: flex;
    align-items: center;
    font-size: .8rem;
    font-weight: 700;
    color: #ff9f43;
  }
  &__count {
    margin-left: .4rem;
    background: #ea5455;
    color: #fff;
    border-radius: 999px;
    padding: 0 7px;
    font-size: .68rem;
  }
  &__list {
    display: flex;
    flex-direction: column;
    gap: .5rem;
    margin-top: .5rem;
    max-height: 260px;
    overflow-y: auto;
  }
}
.apt-request {
  border-left: 4px solid #ff9f43;
  border-radius: .65rem;
  background: #fff;
  box-shadow: 0 2px 8px rgba(34, 41, 47, .08);
  padding: .55rem .6rem;
  transition: transform .15s ease, box-shadow .15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 5px 14px rgba(34, 41, 47, .14);
  }
  &__row {
    display: flex;
    align-items: center;
    gap: .5rem;
  }
  &__avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: .72rem;
    font-weight: 700;
    background: linear-gradient(135deg, #ff9f43, #ffc085);
  }
  &__main { flex: 1; min-width: 0; }
  &__name {
    font-size: .8rem;
    font-weight: 700;
    color: #5e5873;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__meta {
    font-size: .72rem;
    color: #6e6b7b;
    display: flex;
    align-items: center;
  }
  &__actions {
    display: flex;
    gap: .35rem;
    margin-top: .5rem;

    .btn { flex: 1; padding: .3rem .4rem; font-size: .72rem; }
  }
}

/* ===== Main ===== */
.apt-main {
  flex: 1;
  min-width: 0;
  padding: 1.25rem 1.4rem;
}
.apt-main__toolbar {
  display: flex;
  align-items: center;
  gap: .5rem;
  margin-bottom: .5rem;
}
.apt-hamburger { display: none; }
.apt-count {
  display: inline-flex;
  align-items: center;
  font-size: .78rem;
  font-weight: 600;
  color: #6e6b7b;
  background: rgba(115, 103, 240, .08);
  border-radius: 999px;
  padding: .25rem .7rem;
}
.apt-refresh {
  padding: .3rem .45rem !important;
  color: #7367f0 !important;
}
.apt-refresh--spin {
  animation: apt-spin 1s linear infinite;
}
@keyframes apt-spin {
  to { transform: rotate(360deg); }
}

/* ===== Responsive: sidebar becomes an overlay ===== */
@media (max-width: 991.98px) {
  .apt-sidebar {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 10;
    background: #fff;
    border-radius: .5rem;
    box-shadow: 0 6px 24px rgba(34, 41, 47, .15);

    &--open { display: block; }
  }
  .apt-hamburger { display: inline-flex; }
}
</style>

<style lang="scss">
/* Dark mode (unscoped — keyed to body class) */
.dark-layout {
  .apt-sidebar { border-right-color: #3b4253; background: transparent; }
  .apt-type-row { &:hover { background: #343d55; } &__label { color: #d0d2d6; } }
  .apt-type-row__label { color: #d0d2d6; }
  .apt-chip { border-color: #3b4253; color: #b4b7bd; }
  .apt-admin { border-top-color: #3b4253; }
  .apt-admin__switch { color: #d0d2d6; }
  .apt-request { background: #283046; &__name { color: #d0d2d6; } }
  .apt-request__name { color: #d0d2d6; }
  .apt-count { color: #b4b7bd; }

  @media (max-width: 991.98px) {
    .apt-sidebar { background: #283046; }
  }
}
</style>
